/** Messages passagers, annoncés aux lecteurs d'écran via une région `live`. */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ToastTone = "success" | "error" | "info";

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
}

interface ToastContextValue {
  notify: (toast: { tone?: ToastTone; title: string; body?: string }) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const DISPLAY_MS = 7000;

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    ({ tone = "info", title, body }: { tone?: ToastTone; title: string; body?: string }) => {
      counter += 1;
      const id = counter;
      setToasts((current) => [...current, { id, tone, title, body }]);
      window.setTimeout(() => dismiss(id), DISPLAY_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      notify,
      success: (title, body) => notify({ tone: "success", title, body }),
      error: (title, body) => notify({ tone: "error", title, body }),
    }),
    [notify],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <div className="toast__body">
              <div className="toast__title">
                {/* Préfixe textuel : le sens ne dépend pas de la couleur. */}
                <span className="visually-hidden">
                  {toast.tone === "error"
                    ? "Erreur : "
                    : toast.tone === "success"
                      ? "Succès : "
                      : "Information : "}
                </span>
                {toast.title}
              </div>
              {toast.body && <div className="text-muted">{toast.body}</div>}
            </div>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(toast.id)}
              aria-label="Masquer ce message"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast doit être utilisé dans un ToastProvider.");
  return context;
}
